import * as React from "react";
import { useCallback, useMemo } from "react";
import styles from "./styles.module.sass";
import { SelectItem } from "./SelectItem";
import SearchIcon from "@material-ui/icons/Search";
import { translate, InjectedTranslateProps } from "react-i18next";
import * as Strings from "../../../../i18n/strings/MultipleSelect";
import { GenericButtonWithoutIcon } from "../../SharedComponent/GenericButtonWithoutIcon";
import history from "../../../../helpers/history";
import { useRtlClass } from "../../../../lib";
import { useDispatch } from "react-redux";
import { editBranchStatues } from "../../../../redux-store/actions";
import { ConfirmationPopup } from "../ConfirmationPopup/index";
import { submitPickupBranches } from "../../redux-store/actions/pickupBranchesActions";
import { useSelect } from "../../../../hooks/useSelect";
import { PickupStatus } from "../../../../types/shared";
import { useDeepCompareMemo } from "use-deep-compare";
import { useCanUseDriveThruOrdering } from "../../../../hooks/use-can-use-drive-thru-ordering";
import ServiceDisableModal, {
  resolveDisableConfirmBehavior,
  ServiceDisableModalConfirmPayload,
  ServiceDisableModalMode,
} from "../../SetupPickup/BranchesSection/ServiceDisableModal";
import {
  removeScheduledServiceDisable,
  setScheduledServiceDisable,
  setServiceDisableMetadata,
  setServiceEnableOverride,
} from "../../../../redux-store/actions/auditActions";
import {
  ServiceType,
  IScheduledServiceDisable,
} from "../../../../types/audit";
import { usePickupBranchesForm } from "../../../../contexts/PickupBranchesFormContext";
import { scheduledAtToMs, scheduledServiceToMetadata, resolveScheduledUntilMs, isDriveThruScheduleBeforePickup, clampMetadataToMinScheduledUntil, scheduledUntilToMoment } from "../../SetupPickup/BranchesSection/ServiceDisableModal/lib/serviceDisableModalUtils";
import i18n from "../../../../i18n/i18n";
import BranchStrings from "../../../../i18n/strings/branches";
import {
  ISelectedBranchesMap,
} from "../../redux-store/actions/pickupBranchesActions";

interface IMultipleSelectItemData {
  name: string;
  value: string;
  pos_vendor?: string;
  pos_name?: string;
}

interface IMultipleSelectProps {
  items: IMultipleSelectItemData[];
}

const UI_TYPE_TO_SERVICE: Record<string, ServiceType> = {
  Pickup: "pickup",
  Delivery: "delivery",
  DriveThru: "drive_thru",
};

interface PendingToggle {
  item: IMultipleSelectItemData;
  uiType: string;
  serviceType: ServiceType;
}

type SelectedMapField = keyof ISelectedBranchesMap;

const tryParseJson = (str: string): unknown => {
  try {
    return JSON.parse(str);
  } catch {
    return str;
  }
};

const MultipleSelect: React.FC<
  IMultipleSelectProps & InjectedTranslateProps
> = ({ items, t }) => {
  const [searchQuery, setSearchQuery] = React.useState("");
  const [activeButton, setActiveButton] = React.useState(false);
  const [modalVisible, setModalVisible] = React.useState(false);
  const [modalMode, setModalMode] =
    React.useState<ServiceDisableModalMode>("disable");
  const [pendingToggle, setPendingToggle] =
    React.useState<PendingToggle | null>(null);
  const [dismissAllModalVisible, setDismissAllModalVisible] =
    React.useState(false);

  const canUseDriveThruOrdering = useCanUseDriveThruOrdering();
  const dispatch = useDispatch();

  const { selectedBranchesMap, setFieldValue, resetAllFields } = usePickupBranchesForm();

  const scheduledDisables = useSelect(
    (state) => state.serviceAuditReducer.scheduledDisables
  );

  const disableMetadata = useSelect(
    (state) => state.serviceAuditReducer.disableMetadata
  );

  const enableOverrides = useSelect(
    (state) => state.serviceAuditReducer.enableOverrides
  );

  const branches = useSelect(
    (state) => state.branchesReducer.defaultBranches
  );

  const branchNumber = React.useMemo(() => {
    const selectedItemsIds = Object.values(selectedBranchesMap)
      .map((itemsMap: any) =>
        Object.keys(itemsMap || {}).filter((key) => itemsMap[key])
      )
      .reduce((acc, next) => [...acc, ...next], []);
    return new Set(selectedItemsIds).size;
  }, [selectedBranchesMap]);

  const setDriveThruStatus = (params: Record<string, unknown>) =>
    canUseDriveThruOrdering ? params : {};

  const initialBranchesMap = useSelect(
    (state: any) => state.selectedBranchesMapReducer
  ) as ISelectedBranchesMap;

  const applyServiceToggle = useCallback(
    (item: IMultipleSelectItemData, type: string, newVal: boolean) => {
      if (type === "Pickup") {
        setFieldValue("pickup_branch_ids", item.value, newVal);
        if (!newVal && canUseDriveThruOrdering) {
          setFieldValue("drive_thru_branch_ids", item.value, newVal);
        }
        dispatch(
          editBranchStatues({
            pickup_branches_ids: { [item.value]: newVal },
            delivery_branches_ids: {
              [item.value]: selectedBranchesMap.delivery_branch_ids?.[item.value],
            },
            ...setDriveThruStatus({
              drive_thru_branch_ids: {
                [item.value]: newVal
                  ? selectedBranchesMap.drive_thru_branch_ids?.[item.value]
                  : false,
              },
            }),
          })
        );
      } else if (type === "DriveThru") {
        setFieldValue("drive_thru_branch_ids", item.value, newVal);
        if (newVal) {
          setFieldValue("pickup_branch_ids", item.value, true);
        }
        dispatch(
          editBranchStatues({
            drive_thru_branch_ids: { [item.value]: newVal },
            pickup_branches_ids: {
              [item.value]: newVal
                ? true
                : selectedBranchesMap.pickup_branch_ids?.[item.value],
            },
            delivery_branches_ids: {
              [item.value]: selectedBranchesMap.delivery_branch_ids?.[item.value],
            },
          })
        );
      } else if (type === "Delivery") {
        setFieldValue("delivery_branch_ids", item.value, newVal);
        dispatch(
          editBranchStatues({
            delivery_branches_ids: { [item.value]: newVal },
            pickup_branches_ids: {
              [item.value]: selectedBranchesMap.pickup_branch_ids?.[item.value],
            },
            ...setDriveThruStatus({
              drive_thru_branch_ids: {
                [item.value]: selectedBranchesMap.drive_thru_branch_ids?.[item.value],
              },
            }),
          })
        );
      }
    },
    [canUseDriveThruOrdering, dispatch, setFieldValue, selectedBranchesMap]
  );

  const getScheduledForBranch = useCallback(
    (branchId: string, serviceType: ServiceType) =>
      scheduledDisables.find(
        (s) => s.branchId === branchId && s.serviceType === serviceType
      ),
    [scheduledDisables]
  );

  const getPickupScheduledUntil = useCallback(
    (branchId: string): number | null => {
      const scheduled = getScheduledForBranch(branchId, "pickup");
      if (scheduled?.scheduledUntil != null) return scheduled.scheduledUntil;
      const meta = disableMetadata[branchId]?.pickup;
      const branch = branches?.find((b) => b.id === branchId);
      return resolveScheduledUntilMs(
        meta,
        branch?.scheduled_services?.pickup?.scheduled_at
      );
    },
    [getScheduledForBranch, disableMetadata, branches]
  );

  const getDriveThruScheduledUntil = useCallback(
    (branchId: string): number | null => {
      const scheduled = getScheduledForBranch(branchId, "drive_thru");
      if (scheduled?.scheduledUntil != null) return scheduled.scheduledUntil;
      const meta = disableMetadata[branchId]?.drive_thru;
      const branch = branches?.find((b) => b.id === branchId);
      return resolveScheduledUntilMs(
        meta,
        branch?.scheduled_services?.drive_thru?.scheduled_at
      );
    },
    [getScheduledForBranch, disableMetadata, branches]
  );

  // Only revertible (scheduled) disables get the clock icon and the
  // scheduled-edit modal; a permanent (non-revertible) disable shows nothing.
  // A manual enable wins over stale API scheduled_services data until save.
  const isBranchScheduled = useCallback(
    (branchId: string, serviceType: ServiceType) => {
      if (enableOverrides[`${branchId}:${serviceType}`]) return false;

      const scheduled = getScheduledForBranch(branchId, serviceType);
      if (scheduled) return scheduled.is_revertible === true;

      const meta = disableMetadata[branchId]?.[serviceType];
      if (meta) return meta.is_revertible === true;

      const branch = branches?.find((b) => b.id === branchId);
      if (!branch) return false;

      const scheduledService = branch.scheduled_services?.[serviceType];
      return !!(
        scheduledService &&
        (scheduledService.is_revertible === true ||
          scheduledService.scheduled_at != null)
      );
    },
    [getScheduledForBranch, disableMetadata, enableOverrides, branches]
  );

  const handleChange = (
    item: IMultipleSelectItemData,
    type: string,
    newVal: boolean
  ) => {
    const serviceType = UI_TYPE_TO_SERVICE[type];
    if (!serviceType) {
      return;
    }

    if (newVal) {
      dispatch(
        removeScheduledServiceDisable({ branchId: item.value, serviceType })
      );
      dispatch(
        setServiceDisableMetadata({
          branchId: item.value,
          serviceType,
          metadata: null,
        })
      );
      dispatch(
        setServiceEnableOverride({ branchId: item.value, serviceType })
      );
      if (
        type === "DriveThru" &&
        !selectedBranchesMap.pickup_branch_ids?.[item.value]
      ) {
        dispatch(
          removeScheduledServiceDisable({
            branchId: item.value,
            serviceType: "pickup",
          })
        );
        dispatch(
          setServiceDisableMetadata({
            branchId: item.value,
            serviceType: "pickup",
            metadata: null,
          })
        );
        dispatch(
          setServiceEnableOverride({
            branchId: item.value,
            serviceType: "pickup",
          })
        );
      }
      applyServiceToggle(item, type, true);
      return;
    }

    const initialMapKey =
      type === "Pickup"
        ? "pickup_branch_ids"
        : type === "Delivery"
          ? "delivery_branch_ids"
          : "drive_thru_branch_ids";
    const wasInitiallyEnabled =
      initialBranchesMap[initialMapKey]?.[item.value] === true;

    if (wasInitiallyEnabled) {
      setPendingToggle({ item, uiType: type, serviceType });
      setModalMode("disable");
      setModalVisible(true);
    } else {
      dispatch(
        setServiceDisableMetadata({
          branchId: item.value,
          serviceType,
          metadata: null,
        })
      );
      applyServiceToggle(item, type, false);
    }
  };

  const handleScheduledClick = (
    item: IMultipleSelectItemData,
    serviceType: ServiceType
  ) => {
    setPendingToggle({
      item,
      uiType:
        serviceType === "pickup"
          ? "Pickup"
          : serviceType === "delivery"
            ? "Delivery"
            : "DriveThru",
      serviceType,
    });
    setModalMode("scheduled_edit");
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setPendingToggle(null);
  };

  const handleModalConfirm = ({
    metadata,
  }: ServiceDisableModalConfirmPayload) => {
    if (!pendingToggle || !metadata) {
      return;
    }

    const { item, uiType, serviceType } = pendingToggle;
    const { shouldSetScheduled, shouldRemoveScheduled, shouldApplyToggleOff } =
      resolveDisableConfirmBehavior(metadata);

    dispatch(
      setServiceDisableMetadata({
        branchId: item.value,
        serviceType,
        metadata,
      })
    );

    if (shouldSetScheduled) {
      dispatch(
        setScheduledServiceDisable({
          branchId: item.value,
          branchName: item.name,
          serviceType,
          ...metadata,
        })
      );
    }

    if (shouldRemoveScheduled) {
      dispatch(
        removeScheduledServiceDisable({ branchId: item.value, serviceType })
      );
    }

    // Disabling pickup also turns drive-thru off (applyServiceToggle), so
    // mirror the same disable duration onto drive-thru.
    if (
      serviceType === "pickup" &&
      canUseDriveThruOrdering &&
      selectedBranchesMap.drive_thru_branch_ids?.[item.value]
    ) {
      const driveThruMetadata = {
        ...metadata,
        reason: "Disabled because pickup is disabled",
      };

      dispatch(
        setServiceDisableMetadata({
          branchId: item.value,
          serviceType: "drive_thru",
          metadata: driveThruMetadata,
        })
      );

      if (shouldSetScheduled) {
        dispatch(
          setScheduledServiceDisable({
            branchId: item.value,
            branchName: item.name,
            serviceType: "drive_thru",
            ...driveThruMetadata,
          })
        );
      }

      if (shouldRemoveScheduled) {
        dispatch(
          removeScheduledServiceDisable({
            branchId: item.value,
            serviceType: "drive_thru",
          })
        );
      }
    }

    if (serviceType === "pickup" && canUseDriveThruOrdering) {
      const driveThruScheduledUntil = getDriveThruScheduledUntil(item.value);
      if (
        isDriveThruScheduleBeforePickup(
          driveThruScheduledUntil,
          metadata.scheduledUntil
        )
      ) {
        const driveThruMetadata = clampMetadataToMinScheduledUntil(
          {
            ...metadata,
            reason: "Adjusted to match pickup schedule",
          },
          metadata.scheduledUntil
        );

        dispatch(
          setServiceDisableMetadata({
            branchId: item.value,
            serviceType: "drive_thru",
            metadata: driveThruMetadata,
          })
        );

        dispatch(
          setScheduledServiceDisable({
            branchId: item.value,
            branchName: item.name,
            serviceType: "drive_thru",
            ...driveThruMetadata,
          })
        );
      }
    }

    if (shouldApplyToggleOff) {
      applyServiceToggle(item, uiType, false);
    }

    closeModal();
  };

  const handleModalEnable = () => {
    if (!pendingToggle) {
      return;
    }

    const { item, uiType, serviceType } = pendingToggle;
    dispatch(
      removeScheduledServiceDisable({ branchId: item.value, serviceType })
    );
    dispatch(
      setServiceDisableMetadata({
        branchId: item.value,
        serviceType,
        metadata: null,
      })
    );
    dispatch(setServiceEnableOverride({ branchId: item.value, serviceType }));
    if (
      serviceType === "drive_thru" &&
      !selectedBranchesMap.pickup_branch_ids?.[item.value]
    ) {
      dispatch(
        removeScheduledServiceDisable({
          branchId: item.value,
          serviceType: "pickup",
        })
      );
      dispatch(
        setServiceDisableMetadata({
          branchId: item.value,
          serviceType: "pickup",
          metadata: null,
        })
      );
      dispatch(
        setServiceEnableOverride({
          branchId: item.value,
          serviceType: "pickup",
        })
      );
    }
    applyServiceToggle(item, uiType, true);
    closeModal();
  };

  const modalInitialValues = useMemo(() => {
    if (!pendingToggle) {
      return null;
    }
    const existing = getScheduledForBranch(
      pendingToggle.item.value,
      pendingToggle.serviceType
    );
    if (existing) return existing;

    const branchId = pendingToggle.item.value;
    const serviceType = pendingToggle.serviceType;
    const meta = disableMetadata[branchId]?.[serviceType];
    if (meta) {
      return {
        ...meta,
        branchId,
        branchName: pendingToggle.item.name,
        serviceType,
      } as IScheduledServiceDisable;
    }

    const suffix = serviceType === "pickup" ? "pickup"
      : serviceType === "delivery" ? "delivery"
      : "drive_thru";
    const branch = branches?.find((b) => b.id === branchId);
    if (!branch) return null;

    // scheduled_services is what the backend persists, so it's the source of
    // truth after a refresh; the per-suffix fields below are the legacy shape.
    const scheduledService = branch.scheduled_services?.[serviceType];
    if (
      scheduledService &&
      (scheduledService.reason ||
        scheduledService.scheduled_at != null ||
        scheduledService.is_revertible)
    ) {
      return {
        ...scheduledServiceToMetadata(scheduledService),
        branchId,
        branchName: pendingToggle.item.name,
        serviceType,
      } as IScheduledServiceDisable;
    }

    const disableReason = branch[`disable_reason_${suffix}` as keyof typeof branch] as string | undefined;

    const scheduledAt = branch[`scheduled_at_${suffix}` as keyof typeof branch] as string | null | undefined;
    const scheduledUntil = scheduledAtToMs(scheduledAt);
    const isRevertible = branch[`is_revertible_${suffix}` as keyof typeof branch] as boolean | undefined;

    if (!disableReason) return null;

    return {
      reason: disableReason,
      duration: scheduledUntil !== null ? "custom" : "permanent",
      scheduledUntil,
      isPermanent: scheduledUntil === null || !isRevertible,
      isScheduled: scheduledUntil !== null,
      is_revertible: !!isRevertible,
      branchId,
      branchName: pendingToggle.item.name,
      serviceType,
    } as IScheduledServiceDisable;
  }, [pendingToggle, getScheduledForBranch, disableMetadata, branches]);

  const pendingPickupScheduledUntil = pendingToggle
    ? getPickupScheduledUntil(pendingToggle.item.value)
    : null;
  const pendingDriveThruMinDate = useMemo(
    () =>
      pendingToggle?.serviceType === "drive_thru"
        ? scheduledUntilToMoment(pendingPickupScheduledUntil)
        : null,
    [pendingToggle?.serviceType, pendingPickupScheduledUntil]
  );
  const pendingWarningText = (() => {
    if (!pendingToggle) return undefined;
    if (
      pendingToggle.serviceType === "pickup" &&
      selectedBranchesMap.drive_thru_branch_ids?.[pendingToggle.item.value]
    ) {
      return i18n.t(BranchStrings.driveThruDisableHint, { ns: "branches" });
    }
    if (pendingToggle.serviceType === "drive_thru") {
      const hints: string[] = [];
      if (
        modalMode === "scheduled_edit" &&
        !selectedBranchesMap.pickup_branch_ids?.[pendingToggle.item.value]
      ) {
        hints.push(
          i18n.t(BranchStrings.driveThruEnableHint, { ns: "branches" })
        );
      }
      if (pendingPickupScheduledUntil) {
        hints.push(
          i18n.t(BranchStrings.driveThruScheduleMinDateHint, { ns: "branches" })
        );
      }
      return hints.length ? hints.join(" ") : undefined;
    }
    return undefined;
  })();

  const contains = (str: string, strToSearchIn: string): boolean =>
    strToSearchIn.toLowerCase().includes(str.toLowerCase());

  const filterItems = (
    collection: IMultipleSelectItemData[],
    searchValue: string
  ) =>
    collection.filter((item) =>
      contains(searchValue, item.name ? item.name : "")
    );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const searchedItems = React.useMemo(
    () => filterItems(items, searchQuery),
    [searchQuery, items]
  );

  React.useMemo(() => {
    setActiveButton(branchNumber > 0);
  }, [branchNumber]);

  const nextHandler = () =>
    history.push(`/pickup/setup/pickup-branches/complete-branches`);

  const rtl = useRtlClass(styles);
  const [disableConfirmationPopup, setDisableConfirmationPopup] =
    React.useState(false);

  const { status: submitOrderingBranchesStatus } = useSelect(
    (state) => ({
      status: state.submitPickupBranchesReducer.status,
    })
  );

  const allBranchesDisabled = useDeepCompareMemo(
    () =>
      (branches || []).every(
        (branch) =>
          branch.pickup_status === PickupStatus.service_disabled &&
          branch.delivery_status === PickupStatus.service_disabled
      ),
    [branches]
  );

  React.useEffect(() => {
    if (submitOrderingBranchesStatus === "success") {
      setDisableConfirmationPopup(false);
      resetAllFields();
    }
  }, [submitOrderingBranchesStatus, resetAllFields]);

  const disableAllBranches = () => {
    setDisableConfirmationPopup(false);
    if (activeButton && allBranchesDisabled) {
      resetAllFields();
    } else {
      setDismissAllModalVisible(true);
    }
  };

  const handleDismissAllConfirm = ({
    metadata,
  }: ServiceDisableModalConfirmPayload) => {
    if (!metadata) return;

    const activeServiceTypes: ServiceType[] = ["pickup", "delivery"];
    if (canUseDriveThruOrdering) activeServiceTypes.push("drive_thru");

    branches.forEach((branch) => {
      activeServiceTypes.forEach((serviceType) => {
        dispatch(
          setServiceDisableMetadata({
            branchId: branch.id,
            serviceType,
            metadata,
          })
        );
        // Also create scheduled disable entry for metadata to be included in scheduled_services
        dispatch(
          setScheduledServiceDisable({
            branchId: branch.id,
            branchName: branch.name,
            serviceType,
            ...metadata,
          })
        );
      });
    });

    setDismissAllModalVisible(false);
    dispatch(
      submitPickupBranches({
        delivery_branches_ids: [],
        pickup_branches_ids: [],
        ...setDriveThruStatus({ drive_thru_branches_ids: [] }),
      })
    );
  };

  const handleDismissAllCancel = () => {
    setDismissAllModalVisible(false);
  };

  return (
    <div className={`${styles.main} ${rtl}`}>
      {pendingToggle && (
        <ServiceDisableModal
          visible={modalVisible}
          mode={modalMode}
          branchName={pendingToggle.item.name}
          serviceType={pendingToggle.serviceType}
          initialValues={modalInitialValues}
          warningText={pendingWarningText}
          minDate={pendingDriveThruMinDate}
          minDateError={i18n.t(BranchStrings.driveThruScheduleMinDateHint, {
            ns: "branches",
          })}
          onCancel={closeModal}
          onConfirm={handleModalConfirm}
          onEnable={modalMode === "scheduled_edit" ? handleModalEnable : undefined}
        />
      )}
      <ServiceDisableModal
        visible={dismissAllModalVisible}
        mode="disable"
        branchName="All Branches"
        serviceType="Ordering"
        onCancel={handleDismissAllCancel}
        onConfirm={handleDismissAllConfirm}
      />
      <div className={styles.wrapper}>
        <div className={styles.searchWrapper}>
          <input
            type="text"
            onChange={handleInputChange}
            placeholder={t(Strings.searchPlaceholder)}
          />
          <SearchIcon nativeColor="#b2b2b2" />
        </div>
        <div className={styles.headerWrapper}>
          <span className={styles.branchName}>{t(Strings.branchName)}</span>
          <div
            style={{
              justifyContent: canUseDriveThruOrdering
                ? "space-evenly"
                : "space-around",
            }}
            className={styles.typeNameWrapper}
          >
            <span className={styles.typeName}>Pickup</span>
            {canUseDriveThruOrdering && (
              <span className={styles.typeName}>Drive Thru</span>
            )}
            <span className={styles.typeName}>Delivery</span>
          </div>
        </div>
      </div>
      <div className={styles.content}>
        <div className={styles.notFoundBranches}>
          {searchQuery && items.length > 0 && !searchedItems.length && (
            <span className={styles.notExistItem}>
              {t(Strings.notExistItem)}
            </span>
          )}
        </div>
        <div className={styles.branches}>
          {!!searchedItems.length &&
            searchedItems.map((item) => (
              <SelectItem
                key={item.value}
                item={item}
                activeDriveThru={selectedBranchesMap?.drive_thru_branch_ids?.[item.value]}
                activePickup={selectedBranchesMap?.pickup_branch_ids?.[item.value]}
                activeDelivery={selectedBranchesMap?.delivery_branch_ids?.[item.value]}
                scheduledPickup={isBranchScheduled(item.value, "pickup")}
                scheduledDelivery={
                  isBranchScheduled(item.value, "delivery")
                }
                scheduledDriveThru={
                  isBranchScheduled(item.value, "drive_thru")
                }
                onClick={handleChange}
                onScheduledClick={(serviceType) =>
                  handleScheduledClick(item, serviceType)
                }
                pos_vendor={item.pos_vendor}
                pos_name={item.pos_name}
              />
            ))}
        </div>
      </div>
      <div className={styles.btnWrapper}>
        <div className={styles.countWrapper}>
          <span className={styles.count}>{branchNumber}</span>
          <span className={styles.branchSelected}>
            {t(Strings.branchSelected)}
          </span>
        </div>
        <div className={styles.buttonsWrapper}>
          {disableConfirmationPopup && (
            <ConfirmationPopup
              loading={submitOrderingBranchesStatus === "loading"}
              description={t(Strings.disableOrderingQ)}
              open={disableConfirmationPopup}
              onClose={() => setDisableConfirmationPopup(false)}
              onConfirm={disableAllBranches}
              title={t(Strings.disableOrdering)}
              warningIcon={require("../../../../assets/warning2.svg").default}
            />
          )}
          <GenericButtonWithoutIcon
            label={t(Strings.disableAll)}
            className={activeButton ? styles.disableBtn : styles.disabled}
            onClick={() => setDisableConfirmationPopup(true)}
            disabled={!activeButton}
          />
          <GenericButtonWithoutIcon
            label={t(Strings.next)}
            className={`${styles.btn} ${styles.activBtn}`}
            onClick={nextHandler}
          />
        </div>
      </div>
    </div>
  );
};

export default translate("MultipleSelect")(MultipleSelect);
